/* ------------------------------------------------------------------------------------------------
   KOPSTORM GIFT WRAP
   ------------------------------------------------------------------------------------------------
   Companion to snippets/kop-gift-wrap.liquid. Three jobs:

   1. Cart drawer toggle: adds or removes the wrap product through cart/add.js and cart/change.js,
      bundling the same sections the theme bundles so the drawer rerenders through its own
      cart:change flow. The row itself is server rendered from the cart, so state is always true.
   2. Order level enforcement: the wrap is one per order. If it ever ends up with quantity above
      one (quantity stepper is hidden, but other routes exist) it is pinned back to one, and when
      the wrap is the only line left in the cart it is removed.
   3. Product page state: the PDP checkbox rides the theme's own multi item add through the
      custom_upsells classes. When the wrap is already in the cart the magic class is removed so a
      second add is impossible; when the wrap leaves the cart the class returns.

   Delegated on document so the row survives every cart drawer rerender.
------------------------------------------------------------------------------------------------ */
(function () {
  'use strict';

  var busy = false;
  var fixing = false;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function bundledSections() {
    var sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } })
    );
    return sections;
  }

  function loading(state) {
    document.documentElement.dispatchEvent(
      new CustomEvent(state ? 'theme:loading:start' : 'theme:loading:end', { bubbles: true })
    );
  }

  function dispatchCartChange(cart) {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'kop-giftwrap:change', cart: cart }
      })
    );
  }

  function findWrapLine(cart, variantId) {
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].variant_id === variantId) {
        return cart.items[i];
      }
    }
    return null;
  }

  async function addWrap(variantId) {
    var sections = bundledSections();
    var response = await fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1, properties: { _kop_gift_wrap: '1' } }],
        sections: sections.join(',')
      })
    });
    if (!response.ok) {
      throw new Error('add failed');
    }
    var addJson = await response.json();
    var cart = await (await fetch(root() + 'cart.js')).json();
    cart.sections = addJson.sections;
    return cart;
  }

  async function changeWrap(lineKey, quantity) {
    var sections = bundledSections();
    var response = await fetch(root() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: quantity, sections: sections.join(',') })
    });
    if (!response.ok) {
      throw new Error('change failed');
    }
    return response.json();
  }

  /* ---------------------------------------------------------------- cart drawer toggle */

  document.addEventListener('change', async function (event) {
    var input = event.target;
    if (!input.matches('[data-kop-giftwrap-toggle]')) {
      return;
    }
    var row = input.closest('[data-kop-giftwrap]');
    if (!row || busy) {
      // A rerender restores the true state, so a click during a pending request is simply undone.
      input.checked = !input.checked;
      return;
    }

    busy = true;
    input.disabled = true;
    loading(true);

    try {
      var variantId = parseInt(row.getAttribute('data-variant-id'), 10);
      if (input.checked) {
        var cartBefore = await (await fetch(root() + 'cart.js')).json();
        if (!findWrapLine(cartBefore, variantId)) {
          dispatchCartChange(await addWrap(variantId));
        }
      } else {
        var lineKey = row.getAttribute('data-line-key');
        if (lineKey) {
          dispatchCartChange(await changeWrap(lineKey, 0));
        }
      }
    } catch (error) {
      // Resync the drawer from the server so the row never lies about the state.
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } finally {
      loading(false);
      busy = false;
    }
  });

  /* ---------------------------------------------------------------- order level enforcement + PDP sync */

  function syncProductRows(cart) {
    document.querySelectorAll('[data-kop-giftwrap][data-kop-giftwrap-context="product"]').forEach(function (row) {
      var variantId = parseInt(row.getAttribute('data-variant-id'), 10);
      var input = row.querySelector('.kop-giftwrap__input');
      var subtitle = row.querySelector('[data-kop-giftwrap-subtitle]');
      if (!input) {
        return;
      }
      var line = findWrapLine(cart, variantId);
      if (line) {
        row.setAttribute('data-line-key', line.key);
        input.classList.remove('custom_upsells_item_input');
        input.checked = true;
        input.disabled = true;
        if (subtitle) {
          subtitle.textContent = subtitle.getAttribute('data-added-text');
        }
      } else {
        row.removeAttribute('data-line-key');
        input.classList.add('custom_upsells_item_input');
        input.disabled = false;
        input.checked = false;
        if (subtitle) {
          subtitle.textContent = subtitle.getAttribute('data-offer-text');
        }
      }
    });
  }

  document.addEventListener('cart:change', async function (event) {
    var cart = event.detail && event.detail.cart;
    if (!cart || !cart.items) {
      return;
    }

    syncProductRows(cart);

    if (fixing) {
      return;
    }

    var wrapLine = null;
    var row = document.querySelector('[data-kop-giftwrap]');
    if (row) {
      wrapLine = findWrapLine(cart, parseInt(row.getAttribute('data-variant-id'), 10));
    } else {
      // No row on this page: recognise the wrap by the product id printed by theme.liquid, or by the
      // line item property (the product page add through the theme carries no properties).
      for (var i = 0; i < cart.items.length; i++) {
        var properties = cart.items[i].properties || {};
        if (properties._kop_gift_wrap || (window.kopGiftWrapProductId && cart.items[i].product_id === window.kopGiftWrapProductId)) {
          wrapLine = cart.items[i];
          break;
        }
      }
    }

    if (!wrapLine) {
      return;
    }

    var target = null;
    if (cart.items.length === 1) {
      target = 0; // wrap alone in the cart makes no sense
    } else if (wrapLine.quantity > 1) {
      target = 1; // one wrap per order
    }

    if (target === null) {
      return;
    }

    fixing = true;
    try {
      dispatchCartChange(await changeWrap(wrapLine.key, target));
    } catch (error) {
      // Leave it; the next cart interaction rerenders the true state.
    } finally {
      fixing = false;
    }
  });

  // Back or forward navigation restores an old PDP state from the bfcache: resync from the server.
  window.addEventListener('pageshow', async function (event) {
    if (!event.persisted) {
      return;
    }
    try {
      syncProductRows(await (await fetch(root() + 'cart.js')).json());
    } catch (error) {
      /* leave the server rendered state */
    }
  });

  // Storefront diagnostics: run window.KopGiftWrap.diagnose() in the console on a preview.
  window.KopGiftWrap = {
    diagnose: async function () {
      var rows = document.querySelectorAll('[data-kop-giftwrap]');
      var cart = await (await fetch(root() + 'cart.js')).json();
      console.log('[kop-giftwrap] rows on page:', rows.length);
      rows.forEach(function (row) {
        console.log('[kop-giftwrap] row', {
          context: row.getAttribute('data-kop-giftwrap-context'),
          variantId: row.getAttribute('data-variant-id'),
          lineKey: row.getAttribute('data-line-key'),
          inCart: !!findWrapLine(cart, parseInt(row.getAttribute('data-variant-id'), 10))
        });
      });
      console.log('[kop-giftwrap] cart items:', cart.items.map(function (item) {
        return { variant_id: item.variant_id, quantity: item.quantity, properties: item.properties };
      }));
    }
  };
})();
